import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

import { useLanguage } from '../../contexts/LanguageContext';
import { ajouterAuPanier } from '../../utils/panier';
import { quantiteACombler } from '../../utils/reappro';

/**
 * « Tout commander » — le raccourci le plus utile de l'application.
 *
 * 🔴 IL REMPLIT LES PANIERS, IL N'ENVOIE RIEN. Créer les commandes d'un seul
 * clic depuis un tableau de bord serait le geste le plus irréversible de
 * l'application derrière le clic le plus léger. L'utilisateur relit ses paniers
 * — un par fournisseur, rappelés par la carte « à envoyer » — puis envoie.
 *
 * 🔴 ET IL ANNONCE CE QU'IL VA PRODUIRE AVANT DE LE FAIRE. C'est la doctrine du
 * dialogue de réception (RU5-c) : un bouton qui agit sans dire ce qu'il produit
 * oblige à découvrir son effet après coup, sur un autre écran.
 *
 * 🔴 ZÉRO ROUTE NEUVE. `ajouterAuPanier` lit déjà `produit.supplier_id` pour
 * choisir le bon panier, et prend sa quantité de `quantiteACombler` — *le `+1`
 * qui n'est pas une marge de confort mais la définition de l'alerte*. Ce bouton
 * n'est qu'une boucle.
 *
 * ⚠️ ET IL DIT CE QU'IL ÉCARTE. Un produit sans fournisseur ne peut pas entrer
 * dans un panier : `PurchaseOrder.supplier_id` est non nullable, un panier
 * mêlé ne deviendrait jamais une commande. Ces produits sont écartés **et
 * nommés** — même règle que `revaliderPanier` : *un panier qui se répare en
 * silence est un panier qui ment.* ⚠️ Aujourd'hui la base de développement n'en
 * porte aucun : le décor cache le cas, la garde ne le laisse pas passer.
 */
const BoutonToutCommander = ({ produits, stockId, fournisseurs, onFait }) => {
  const { t } = useLanguage();
  const [ouvert, setOuvert] = useState(false);

  // ── CE QUE LE GESTE VA PRODUIRE, CALCULÉ AVANT DE L'OUVRIR ───────────────
  const retenus = (produits || []).filter((p) => p.supplier_id && quantiteACombler(p) > 0);
  const ecartes = (produits || []).filter((p) => !p.supplier_id);

  const parFournisseur = new Map();
  retenus.forEach((p) => {
    const cle = String(p.supplier_id);
    if (!parFournisseur.has(cle)) parFournisseur.set(cle, []);
    parFournisseur.get(cle).push(p);
  });

  const nomFournisseur = (id) =>
    (fournisseurs || []).find((f) => String(f.id) === String(id))?.company_name
    || `#${id}`;

  const remplir = () => {
    retenus.forEach((p) => ajouterAuPanier(stockId, p));
    setOuvert(false);
    if (onFait) onFait({ paniers: parFournisseur.size, lignes: retenus.length,
                         ecartes: ecartes.length });
  };

  // ⚠️ UN BOUTON QUI NE PEUT RIEN FAIRE NE S'AFFICHE PAS ACTIF. *Un bouton qui
  // ne fait rien est pire qu'un bouton absent : il promet.*
  const inerte = retenus.length === 0;

  return (
    <>
      <Button
        variant="contained"
        startIcon={<ShoppingCartIcon />}
        disabled={inerte}
        onClick={() => setOuvert(true)}
      >
        {t('dashboard.orderAll')}
      </Button>

      <Dialog open={ouvert} onClose={() => setOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dashboard.orderAllTitle')}</DialogTitle>
        <DialogContent>
          {/* 🔴 LE DÉCOMPTE EST ANNONCÉ EN TÊTE : combien de paniers, combien de
              lignes. C'est la phrase que l'utilisateur doit pouvoir contredire
              avant de cliquer, pas après. */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('dashboard.orderAllSummary', {
              carts: parFournisseur.size, lines: retenus.length,
            })}
          </Typography>

          <List dense disablePadding>
            {[...parFournisseur.entries()].map(([id, lignes]) => (
              <ListItem key={id} disableGutters>
                <ListItemText
                  primary={nomFournisseur(id)}
                  secondary={lignes
                    .map((p) => `${p.name} × ${quantiteACombler(p)}`)
                    .join(' · ')}
                />
              </ListItem>
            ))}
          </List>

          {ecartes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('dashboard.orderAllSkipped', { count: ecartes.length })}
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {ecartes.map((p) => <li key={p.id}>{p.name}</li>)}
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOuvert(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={remplir} disabled={inerte}>
            {t('dashboard.orderAllConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BoutonToutCommander;
